import { showInstantCart, sizeChoices } from './pf-cart-instant.js?v=20261030h';

/**
 * Instant cart drawer for every product page.
 *
 * The page's own add-to-cart scripts differ per product type (vests, USB jacket, Nordik, comforter, pillows,
 * Horizon product form…). Rather than changing each one, this watches the cart add request itself: the moment
 * a product page asks Shopify to add one of its variants, the line is shown in the drawer (pf-cart-instant)
 * and the page's script carries on as before; its server render then morphs over the provisional line.
 *
 * Skipped when another script already showed the line (apparel, gear, slippers, gloves, cart upsell),
 * for size changes and free accessories, and for "Jetzt kaufen" (straight to checkout).
 */
const host = document.querySelector('[data-pf-instant-auto]');
if (host && !window.pfInstantAuto) {
  window.pfInstantAuto = true;
  let product = null;
  try { product = JSON.parse(host.querySelector('[data-pf-instant-product]')?.textContent || 'null'); } catch (e) { product = null; }

  // Remember a "buy now" click: those flows go to the checkout, the drawer must not flash open.
  let buyNowAt = 0;
  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-buy-now], .pf-buy-now, [name="buy_now"]')) buyNowAt = Date.now();
  }, true);

  const isCartAdd = (input) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';
    return /\/cart\/add(\.js)?(\?|$)/.test(url);
  };

  /** @returns {Array<{id: string, quantity: number, properties: Record<string, string>, parent?: boolean}>} */
  function readItems(body) {
    if (typeof body === 'string') {
      const data = JSON.parse(body);
      const list = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
      return list.map((item) => ({ id: String(item.id), quantity: Number(item.quantity) || 1, properties: item.properties || {}, parent: !!(item.parent_id || item.parent_line_key) }));
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const id = body.get('id');
      if (!id) return [];
      const properties = {};
      for (const [key, value] of body.entries()) {
        const m = /^properties\[(.+)\]$/.exec(key);
        if (m) properties[m[1]] = String(value);
      }
      return [{ id: String(id), quantity: Number(body.get('quantity')) || 1, properties, parent: false }];
    }
    return [];
  }

  function show(input, init) {
    if (!product || !init?.body || !isCartAdd(input) || Date.now() - buyNowAt < 1500) return null;
    const component = document.querySelector('cart-drawer-component cart-items-component');
    if (!component || component.classList.contains('pf-instant-pending')) return null;
    const items = readItems(init.body);
    if (!items.length) return null;
    // Garments may come with their free accessories in the same request (attached by parent): those become gift rows.
    const accessories = items.filter((i) => i.parent || i.properties._PyreFitGift);
    const garments = items.filter((i) => !accessories.includes(i));
    // Accessory-only requests (pack gifts), size changes (they carry their stamp) and upsell lines are handled elsewhere.
    if (!garments.length || garments.some((i) => i.properties._PyreFitAt || i.properties._PyreFitUpsell)) return null;
    const rowTpl = host.querySelector('template[data-pf-instant="row"]');
    const names = (rowTpl?.dataset.optionNames || '').split('|');
    const lines = [];
    for (const item of garments) {
      const variant = product.variants.find((v) => String(v.id) === item.id);
      if (!variant) return null;
      lines.push({ variantId: variant.id, options: variant.options, quantity: item.quantity, unitPrice: variant.price, compareAt: variant.compare_at_price || 0, sizes: sizeChoices(product.variants, names, variant) });
    }
    // Free accessories: those sent with the garment, else the power bank + laundry net of garments that get them
    // in a second request (pack accessories beyond those arrive with the server render).
    const byKind = {};
    for (const a of accessories) {
      const kind = a.properties._PyreFitGiftKind;
      if (kind) byKind[kind] = (byKind[kind] || 0) + a.quantity;
    }
    const giftCount = garments.filter((i) => i.properties._PyreFitRealGifts === '1').reduce((sum, i) => sum + i.quantity, 0);
    if (!accessories.length && giftCount) { byKind.battery = giftCount; byKind.net = giftCount; }
    const gifts = Object.entries(byKind).map(([kind, count]) => ({ kind, count }));
    return showInstantCart({ host, lines, gifts, format: (cents) => (cents / 100).toFixed(2) });
  }

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    let instant = null;
    try { instant = show(input, init); } catch (e) { instant = null; }
    const request = nativeFetch.call(this, input, init);
    if (!instant) return request;
    return request.then(
      (response) => { if (!response.ok) instant.revert(); instant.done(); return response; },
      (error) => { instant.revert(); instant.done(); throw error; },
    );
  };
}
